/**
 * ActiveStepCard
 *
 * Left-panel card shown during content generation.
 * Displays the current active step with its icon, name, live message,
 * a per-step elapsed timer, and a grayed list of remaining steps.
 */

import { useEffect, useState } from 'react'
import type { StepDef } from './GenerationSteps'

interface ActiveStepCardProps {
  step: StepDef | null
  message: string | null
  startedAt: number
  pendingSteps: StepDef[]
}

function formatElapsed(ms: number): string {
  const totalSeconds = Math.floor(ms / 1000)
  const minutes = Math.floor(totalSeconds / 60)
  const seconds = totalSeconds % 60
  if (minutes > 0) return `${minutes}m ${seconds}s`
  return `${seconds}s`
}

export function ActiveStepCard({ step, message, startedAt, pendingSteps }: ActiveStepCardProps) {
  const [elapsed, setElapsed] = useState(0)

  useEffect(() => {
    setElapsed(0)
    const interval = setInterval(() => {
      setElapsed(Date.now() - startedAt)
    }, 1000)
    return () => clearInterval(interval)
  }, [startedAt])

  return (
    <div className="flex flex-col h-full p-5 gap-5">
      {/* Active step hero */}
      {step ? (
        <div className="flex flex-col items-center text-center gap-3 pt-4">
          <div className="flex h-14 w-14 items-center justify-center rounded-full border-2 border-emerald-500 bg-emerald-50 text-emerald-600 animate-pulse [&>svg]:w-6 [&>svg]:h-6">
            {step.icon}
          </div>
          <div>
            <p className="text-xs font-medium uppercase tracking-widest text-emerald-600">
              En progreso
            </p>
            <h3 className="mt-0.5 text-base font-semibold text-slate-800">{step.label}</h3>
          </div>
          {message && (
            <p className="text-sm text-slate-500 leading-snug">{message}</p>
          )}
          <span className="text-xs tabular-nums text-slate-400">{formatElapsed(elapsed)}</span>
        </div>
      ) : (
        <div className="flex flex-col items-center text-center gap-2 pt-4">
          <div className="h-14 w-14 rounded-full border-2 border-slate-200 bg-slate-50 animate-pulse" />
          <p className="text-sm text-slate-400">Iniciando...</p>
        </div>
      )}

      {/* Remaining steps */}
      {pendingSteps.length > 0 && (
        <div className="flex flex-col gap-1.5 mt-auto">
          <p className="text-xs uppercase tracking-widest text-slate-300 mb-0.5">Pendiente</p>
          {pendingSteps.map((s) => (
            <div key={s.id} className="flex items-center gap-2 text-xs text-slate-300">
              <span className="[&>svg]:w-3.5 [&>svg]:h-3.5">{s.icon}</span>
              <span>{s.label}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
